import { ExternalLink, Play, RefreshCw, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";

import { SourceOperationsSheet } from "@/components/sources/source-operations-sheet";
import { SourcePauseMenu } from "@/components/sources/source-pause-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { OperatorSourceStatus, SourceConfig, VendorPage } from "@/lib/api";
import type { ScraperGroup, ScraperRowModel } from "@/lib/scraper-catalog";
import { cn } from "@/lib/utils";

export function VendorFilter({
  groups,
  selectedVendor,
  onSelect,
}: {
  groups: ScraperGroup[];
  selectedVendor: string;
  onSelect: (vendor: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        className={filterButtonClass(selectedVendor === "all")}
        onClick={() => onSelect("all")}
      >
        All vendors
      </button>
      {groups.map((group) => (
        <button
          key={group.vendorKey}
          type="button"
          className={filterButtonClass(selectedVendor === group.vendorKey)}
          onClick={() => onSelect(group.vendorKey)}
        >
          {group.vendorLabel}
          <span className="text-muted-fg">{group.rows.length}</span>
        </button>
      ))}
    </div>
  );
}

export function ScraperRow({
  row,
  running,
  syncing,
  analyzing,
  linking,
  linkCandidates,
  onRun,
  onSync,
  onAnalyze,
  onLinkVendorPage,
}: {
  row: ScraperRowModel;
  running: boolean;
  syncing: boolean;
  analyzing: boolean;
  linking: boolean;
  linkCandidates: SourceConfig[];
  onRun: (source: SourceConfig) => void;
  onSync: (source: SourceConfig) => void;
  onAnalyze: (source: SourceConfig) => void;
  onLinkVendorPage: (source: SourceConfig, page: VendorPage) => void;
}) {
  const page = row.page;
  const source = row.source;
  const processing = source ? processingFromSource(source) : null;
  const worker = row.status ? workerState(row.status) : null;

  return (
    <div className="grid gap-3 border-b border-border/60 bg-bg/40 p-3 last:border-b-0 md:grid-cols-[minmax(0,1fr)_minmax(240px,0.85fr)] xl:grid-cols-[minmax(0,1.2fr)_minmax(260px,0.8fr)_auto]">
      <div className="min-w-0 space-y-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-medium">
            {page?.label ?? source?.display_name ?? row.key}
          </span>
          {page ? <Badge variant="outline">{page.page_type}</Badge> : null}
          {source?.is_active === false ? <Badge variant="outline">inactive</Badge> : null}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {page ? <CapabilityBadges page={page} /> : <Badge variant="warn">no vendor page</Badge>}
        </div>
        <div className="truncate text-xs text-muted-fg">
          {page?.url ?? source?.source_url ?? "No page URL configured"}
        </div>
      </div>

      <div className="min-w-0 space-y-2 text-xs">
        {source ? (
          <>
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant="info">{source.id}</Badge>
              <Badge variant="outline">{source.cadence}</Badge>
              <Badge variant="outline">{runtimeMode(source)}</Badge>
              {processing ? (
                <Badge variant={processing.auto_extract ? "success" : "outline"}>
                  {processing.auto_extract ? "auto analyze" : "manual analyze"}
                </Badge>
              ) : null}
              {processing ? <Badge variant="outline">group {processing.group_size}</Badge> : null}
              {worker ? <Badge variant={worker.variant}>{worker.label}</Badge> : null}
            </div>
            <div className="text-muted-fg">
              {row.status?.last_audit_event ? (
                <span>
                  last scrape:{" "}
                  {auditSummary(
                    row.status.last_audit_event.created_at,
                    row.status.last_audit_event.status,
                  )}
                </span>
              ) : (
                <span>last scrape: none recorded</span>
              )}
            </div>
          </>
        ) : (
          <UnlinkedPageControl
            page={page}
            candidates={linkCandidates}
            linking={linking}
            onLink={onLinkVendorPage}
          />
        )}
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-0.5 md:col-span-2 xl:col-span-1 xl:justify-end">
        {page ? (
          <a
            href={page.url}
            target="_blank"
            rel="noreferrer"
            className={compactActionClass}
          >
            <ExternalLink className="size-3.5" />
            Open
          </a>
        ) : null}
        {source ? (
          <>
            <Button
              size="sm"
              variant="outline"
              className={compactActionClass}
              onClick={() => onRun(source)}
              disabled={running}
              title="Capture this scraper config now"
            >
              <Play />
              Run
            </Button>
            <Button
              size="sm"
              variant="outline"
              className={compactActionClass}
              onClick={() => onAnalyze(source)}
              disabled={analyzing}
              title="Analyze existing new, unstructured inbox rows for this source"
            >
              <Sparkles />
              Analyze
            </Button>
            <Button
              size="sm"
              variant="outline"
              className={compactActionClass}
              onClick={() => onSync(source)}
              disabled={syncing}
              title="Push this DB config to content-scraper"
            >
              <RefreshCw />
              Sync
            </Button>
            <SourceOperationsSheet source={source} triggerClassName={compactActionClass} />
            <SourcePauseMenu source={source} triggerClassName={compactActionClass} />
          </>
        ) : null}
      </div>
    </div>
  );
}

const compactActionClass =
  "inline-flex h-7 shrink-0 items-center justify-center gap-1.5 rounded border border-border bg-transparent px-2 text-[11px] font-medium hover:bg-muted [&_svg]:size-3.5";

function UnlinkedPageControl({
  page,
  candidates,
  linking,
  onLink,
}: {
  page: VendorPage | null;
  candidates: SourceConfig[];
  linking: boolean;
  onLink: (source: SourceConfig, page: VendorPage) => void;
}) {
  const [selectedSourceId, setSelectedSourceId] = useState(candidates[0]?.id ?? "");

  useEffect(() => {
    setSelectedSourceId(candidates[0]?.id ?? "");
  }, [candidates]);

  if (!page) {
    return (
      <div className="rounded-md border border-border/60 px-3 py-2 text-muted-fg">
        No vendor page linked to this scraper config.
      </div>
    );
  }

  if (!page.capabilities.scrape) {
    return (
      <div className="rounded-md border border-border/60 px-3 py-2 text-muted-fg">
        No scraper config linked to this vendor page.
      </div>
    );
  }

  if (candidates.length === 0) {
    return (
      <div className="rounded-md border border-border/60 px-3 py-2 text-muted-fg">
        No unlinked scraper config available for this vendor.
      </div>
    );
  }

  const selected = candidates.find((source) => source.id === selectedSourceId) ?? candidates[0];
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border/60 px-2 py-2">
      <select
        value={selectedSourceId}
        onChange={(event) => setSelectedSourceId(event.target.value)}
        className="h-7 min-w-0 flex-1 rounded border border-border bg-bg px-2 text-xs text-fg outline-none focus:border-accent"
        aria-label="Scraper config to link"
      >
        {candidates.map((source) => (
          <option key={source.id} value={source.id}>
            {source.display_name}
          </option>
        ))}
      </select>
      <Button
        size="sm"
        variant="outline"
        className={compactActionClass}
        disabled={linking || !selected}
        onClick={() => onLink(selected, page)}
      >
        Link
      </Button>
    </div>
  );
}

function filterButtonClass(active: boolean): string {
  return cn(
    "inline-flex h-8 items-center gap-2 rounded-md border px-3 text-xs font-medium",
    active
      ? "border-accent bg-accent/15 text-accent"
      : "border-border text-muted-fg hover:bg-muted hover:text-fg",
  );
}

function CapabilityBadges({ page }: { page: VendorPage }) {
  const caps = page.capabilities;
  return (
    <>
      <Badge variant={caps.scrape ? "success" : "outline"}>scrape {caps.scrape ? "yes" : "no"}</Badge>
      <Badge variant={caps.warm_page ? "info" : "outline"}>warm {caps.warm_page ? "yes" : "no"}</Badge>
      <Badge variant={caps.embed ? "info" : "outline"}>embed {caps.embed ? "yes" : "no"}</Badge>
      <Badge variant={caps.open ? "outline" : "warn"}>open {caps.open ? "yes" : "no"}</Badge>
    </>
  );
}

function processingFromSource(source: SourceConfig): { auto_extract: boolean; group_size: number } {
  const processing = source.config?.processing;
  if (!isRecord(processing)) return { auto_extract: false, group_size: 1 };
  return {
    auto_extract: processing.auto_extract === true,
    group_size: boundedNumber(processing.group_size, 1, 1, 20),
  };
}

function runtimeMode(source: SourceConfig): string {
  const runtime = source.config?.runtime;
  if (!isRecord(runtime) || typeof runtime.mode !== "string") return "poll";
  return runtime.mode;
}

function workerState(status: OperatorSourceStatus): {
  label: string;
  variant: "success" | "warn" | "danger" | "info" | "outline";
} {
  if (status.worker.error) return { label: "worker error", variant: "danger" };
  if (status.worker.running) return { label: "running", variant: "info" };
  if (!status.worker.configured) return { label: "not synced", variant: "warn" };
  if (status.worker.scheduled) return { label: "scheduled", variant: "success" };
  if (status.worker.last_error) return { label: "last run failed", variant: "danger" };
  return { label: "synced", variant: "info" };
}

function auditSummary(createdAt: string, status: string): string {
  const when = new Intl.DateTimeFormat(undefined, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(createdAt));
  return `${status} ${when}`;
}

function boundedNumber(value: unknown, fallback: number, min: number, max: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.min(Math.max(value, min), max);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
